/**
 * ArchiveIntakeModal — the archive intake cover sheet (drafts/57 ARC-02).
 * Captures title/type/dept/site/owner/date/confidentiality/reason/source/
 * storage + the risk class. The required approver role is DERIVED from the
 * risk class (shown live); the server re-derives it authoritatively. On submit
 * the record lands PENDING_APPROVAL.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, useToast } from '@edms/design-system';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import {
  ARCHIVE_RECORD_TYPES, RISK_CLASSES, CONFIDENTIALITIES, STORAGE_MEDIA,
  APPROVER_BY_RISK, RISK_STYLE, type ArchiveRecord, type RiskClass,
  type Confidentiality, type StorageMedium,
} from './types';

interface SitesResp { items: { site_id: string; name: string; site_code?: string }[] }
interface DeptsResp { items: { department_id: string; name: string }[] }
interface PoliciesResp { items: { policy_id: string; name: string }[] }

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '5vh 16px', zIndex: 1000, overflowY: 'auto',
};
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const label: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted, #475569)' };
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-subtle, #d1d5db)', fontSize: 14, width: '100%',
};

export function ArchiveIntakeModal({ onClose, onCreated }: { onClose: () => void; onCreated: (r: ArchiveRecord) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const [title, setTitle] = useState('');
  const [recordType, setRecordType] = useState<string>('SOP');
  const [riskClass, setRiskClass] = useState<RiskClass>('MEDIUM');
  const [confidentiality, setConfidentiality] = useState<Confidentiality>('INTERNAL');
  const [storageMedium, setStorageMedium] = useState<StorageMedium>('ELECTRONIC');
  const [siteId, setSiteId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [retentionPolicyId, setRetentionPolicyId] = useState('');
  const [sourceSystem, setSourceSystem] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [archiveReason, setArchiveReason] = useState('');

  const sites = useQuery<SitesResp>({ queryKey: ['sites'], queryFn: ({ signal }) => apiFetch('/api/v1/sites', { signal }), staleTime: 300_000 });
  const depts = useQuery<DeptsResp>({ queryKey: ['departments-kpis'], queryFn: ({ signal }) => apiFetch('/api/v1/departments/kpis', { signal }), staleTime: 300_000 });
  const policies = useQuery<PoliciesResp>({ queryKey: ['retention-policies'], queryFn: ({ signal }) => apiFetch('/api/v1/retention/policies', { signal }), staleTime: 300_000 });

  const create = useMutation({
    mutationFn: () => apiFetch<ArchiveRecord>('/api/v1/archive/records', {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
      body: {
        title: title.trim(), record_type: recordType, risk_class: riskClass,
        confidentiality, storage_medium: storageMedium,
        site_id: siteId || undefined, department_id: departmentId || undefined,
        retention_policy_id: retentionPolicyId || undefined,
        source_system: sourceSystem.trim() || undefined,
        storage_location: storageLocation.trim() || undefined,
        archive_reason: archiveReason.trim() || undefined,
      },
    }),
    onSuccess: (rec) => {
      toast.success(t('archive.intake.created', { defaultValue: 'Archive record submitted for approval.' }));
      qc.invalidateQueries({ queryKey: ['archive'] });
      onCreated(rec);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Create failed';
      toast.error(t('archive.intake.createFailed', { defaultValue: 'Could not create archive record' }), msg);
    },
  });

  const canSubmit = title.trim().length > 2 && !!recordType && !create.isPending;
  const derivedApprover = APPROVER_BY_RISK[riskClass];

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={{ width: 'min(720px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <Card>
          <CardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{t('archive.intake.title', { defaultValue: 'New archive intake' })}</h2>
              <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7989' }}>×</button>
            </div>
          </CardHeader>
          <CardBody>
            <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) create.mutate(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={field}>
                <label style={label} htmlFor="arc-title">{t('archive.intake.recordTitle', { defaultValue: 'Record title *' })}</label>
                <input id="arc-title" style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. BMR — Paracetamol Batch PCM-2025-0021" required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={field}>
                  <label style={label} htmlFor="arc-type">{t('archive.intake.type', { defaultValue: 'Record type *' })}</label>
                  <select id="arc-type" style={inputStyle} value={recordType} onChange={(e) => setRecordType(e.target.value)}>
                    {ARCHIVE_RECORD_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                  </select>
                </div>
                <div style={field}>
                  <label style={label} htmlFor="arc-risk">{t('archive.intake.risk', { defaultValue: 'Risk class' })}</label>
                  <select id="arc-risk" style={inputStyle} value={riskClass} onChange={(e) => setRiskClass(e.target.value as RiskClass)}>
                    {RISK_CLASSES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ background: RISK_STYLE[riskClass].bg, color: RISK_STYLE[riskClass].fg, borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>
                {t('archive.intake.approverHint', { defaultValue: 'Risk-based approval' })}: {riskClass} → {t('archive.intake.requires', { defaultValue: 'requires' })} <strong>{derivedApprover}</strong>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={field}>
                  <label style={label} htmlFor="arc-site">{t('archive.intake.site', { defaultValue: 'Site' })}</label>
                  <select id="arc-site" style={inputStyle} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                    <option value="">{t('common.none', { defaultValue: '— none —' })}</option>
                    {sites.data?.items.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
                  </select>
                </div>
                <div style={field}>
                  <label style={label} htmlFor="arc-dept">{t('archive.intake.dept', { defaultValue: 'Department' })}</label>
                  <select id="arc-dept" style={inputStyle} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                    <option value="">{t('common.none', { defaultValue: '— none —' })}</option>
                    {depts.data?.items.map((d) => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={field}>
                  <label style={label} htmlFor="arc-conf">{t('archive.intake.confidentiality', { defaultValue: 'Confidentiality' })}</label>
                  <select id="arc-conf" style={inputStyle} value={confidentiality} onChange={(e) => setConfidentiality(e.target.value as Confidentiality)}>
                    {CONFIDENTIALITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={field}>
                  <label style={label} htmlFor="arc-medium">{t('archive.intake.storageMedium', { defaultValue: 'Storage medium' })}</label>
                  <select id="arc-medium" style={inputStyle} value={storageMedium} onChange={(e) => setStorageMedium(e.target.value as StorageMedium)}>
                    {STORAGE_MEDIA.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div style={field}>
                <label style={label} htmlFor="arc-policy">{t('archive.intake.retention', { defaultValue: 'Retention policy' })}</label>
                <select id="arc-policy" style={inputStyle} value={retentionPolicyId} onChange={(e) => setRetentionPolicyId(e.target.value)}>
                  <option value="">{t('common.none', { defaultValue: '— none —' })}</option>
                  {policies.data?.items.map((p) => <option key={p.policy_id} value={p.policy_id}>{p.name}</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={field}>
                  <label style={label} htmlFor="arc-source">{t('archive.intake.source', { defaultValue: 'Source system' })}</label>
                  <input id="arc-source" style={inputStyle} value={sourceSystem} onChange={(e) => setSourceSystem(e.target.value)} placeholder="e.g. MES — Batch Execution" />
                </div>
                <div style={field}>
                  <label style={label} htmlFor="arc-loc">{t('archive.intake.location', { defaultValue: 'Storage location' })}</label>
                  <input id="arc-loc" style={inputStyle} value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} placeholder="e.g. Off-site vault Box A-12" />
                </div>
              </div>

              <div style={field}>
                <label style={label} htmlFor="arc-reason">{t('archive.intake.reason', { defaultValue: 'Archive reason' })}</label>
                <textarea id="arc-reason" style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} placeholder="Why is this record being archived?" />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600 }}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button type="submit" disabled={!canSubmit} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: canSubmit ? 'var(--color-primary, #2563EB)' : '#9CA3AF', color: 'white', cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
                  {create.isPending ? t('common.submitting', { defaultValue: 'Submitting…' }) : t('archive.intake.submit', { defaultValue: 'Submit for approval' })}
                </button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
